// ---------------------------------------------------------------------------
// Channels page — PERIOD persistence helpers (F4 / Q8).
//
// - Reads the last chosen preset from localStorage on page mount.
// - Falls back to "30d" on first visit, invalid stored values, or when
//   localStorage is unavailable (SSR, privacy mode, quota errors).
// - Persists only preset names. "custom" ranges are intentionally not
//   restored across reloads — they reset to the default on next visit.
// ---------------------------------------------------------------------------

import type { TimePeriod } from '@/components/v11/discovery/types';

export const CHANNELS_PERIOD_STORAGE_KEY = 'v11.channels.period';
export const DEFAULT_CHANNELS_PERIOD: TimePeriod = '30d';

/**
 * Presets we allow to persist. `custom` is excluded by design (Q8):
 * custom-range reloads fall back to the default.
 */
const PERSISTABLE_PRESETS: ReadonlySet<TimePeriod> = new Set<TimePeriod>([
  'today',
  'yesterday',
  '7d',
  '15d',
  '30d',
  '3m',
  '6m',
  '12m',
  'wtd',
  'mtd',
  'qtd',
  'ytd',
  'all',
]);

function isPersistablePreset(value: string): value is TimePeriod {
  return PERSISTABLE_PRESETS.has(value as TimePeriod);
}

// Module-level flag so we warn at most once per page lifetime about a
// corrupt stored value — avoids spamming the console on every read.
let warnedOnInvalid = false;

export function readStoredChannelsPeriod(): TimePeriod {
  try {
    const raw = localStorage.getItem(CHANNELS_PERIOD_STORAGE_KEY);
    if (raw === null) return DEFAULT_CHANNELS_PERIOD;
    if (isPersistablePreset(raw)) return raw;

    if (!warnedOnInvalid) {
      warnedOnInvalid = true;
      console.warn(
        `[channels] Unrecognised period "${raw}" in localStorage; falling back to ${DEFAULT_CHANNELS_PERIOD}.`
      );
    }
    return DEFAULT_CHANNELS_PERIOD;
  } catch {
    // SSR, privacy mode, disabled storage — silently fall back.
    return DEFAULT_CHANNELS_PERIOD;
  }
}

export function writeStoredChannelsPeriod(period: TimePeriod): void {
  // Custom ranges never persist: spec calls for a fresh default on each visit.
  if (period === 'custom') return;
  if (!isPersistablePreset(period)) return;

  try {
    localStorage.setItem(CHANNELS_PERIOD_STORAGE_KEY, period);
  } catch {
    // Quota errors / disabled storage — best-effort, nothing else to do.
  }
}
